import {
  FulfilmentPicPurpose,
  FulfilmentPicWeekday,
  resolvePicPurposes,
  type FulfilmentPicProfile,
} from '../../../models/FulfilmentPicConfig';

// Mirrors the Facade FulfilmentPicSelector so the admin sees who is on duty the way routing does.
const SGT_OFFSET_MS = 8 * 60 * 60 * 1000;
const WEEKDAYS_FROM_SUNDAY = [
  FulfilmentPicWeekday.SUN,
  FulfilmentPicWeekday.MON,
  FulfilmentPicWeekday.TUE,
  FulfilmentPicWeekday.WED,
  FulfilmentPicWeekday.THU,
  FulfilmentPicWeekday.FRI,
  FulfilmentPicWeekday.SAT,
] as const;

export const picPurposeLabels: Record<FulfilmentPicPurpose, string> = {
  [FulfilmentPicPurpose.COUPANG_PURCHASE]: 'Coupang purchase',
  [FulfilmentPicPurpose.NON_COUPANG_PURCHASE]: 'Non-Coupang purchase',
  [FulfilmentPicPurpose.BULK_PURCHASE]: 'Bulk purchase',
  [FulfilmentPicPurpose.GIG_HANDBACK]: 'Gig hand-back',
};

export const picPurposeDescriptions: Record<FulfilmentPicPurpose, string> = {
  [FulfilmentPicPurpose.COUPANG_PURCHASE]:
    'Orders whose lines left to buy internally have Coupang links, including restricted brands. Lines a gig worker skipped or handed back come here only while no Gig hand-back PIC is designated.',
  [FulfilmentPicPurpose.NON_COUPANG_PURCHASE]:
    'Orders whose lines left to buy internally have no Coupang link (Naver, Olive Young, brand official stores).',
  [FulfilmentPicPurpose.BULK_PURCHASE]: 'Orders of 30 or more units.',
  [FulfilmentPicPurpose.GIG_HANDBACK]: 'Lines a gig worker skipped or handed back to buy internally.',
};

export enum PicDutyStatus {
  ON_DUTY = 'ON_DUTY',
  INACTIVE = 'INACTIVE',
  NO_NAME = 'NO_NAME',
  ZERO_WEIGHT = 'ZERO_WEIGHT',
  ON_LEAVE = 'ON_LEAVE',
  OFF_TODAY = 'OFF_TODAY',
}

export const picDutyStatusLabels: Record<PicDutyStatus, string> = {
  [PicDutyStatus.ON_DUTY]: 'on duty',
  [PicDutyStatus.INACTIVE]: 'inactive',
  [PicDutyStatus.NO_NAME]: 'no name',
  [PicDutyStatus.ZERO_WEIGHT]: 'weight 0',
  [PicDutyStatus.ON_LEAVE]: 'on leave',
  [PicDutyStatus.OFF_TODAY]: 'off today',
};

export interface PicDuty {
  pic: FulfilmentPicProfile;
  status: PicDutyStatus;
}

export interface PicPurposeCoverage {
  /** Null groups PICs without a purpose. */
  purpose: FulfilmentPicPurpose | null;
  members: PicDuty[];
  hasMemberOnDuty: boolean;
}

export interface PicRosterCoverage {
  dateSgt: string;
  groups: PicPurposeCoverage[];
}

const unavailableStatus = (pic: FulfilmentPicProfile, dateSgt: string): PicDutyStatus | null => {
  if (!pic.isActive) {
    return PicDutyStatus.INACTIVE;
  }
  if (pic.name.trim().length === 0) {
    return PicDutyStatus.NO_NAME;
  }
  if (pic.weight <= 0) {
    return PicDutyStatus.ZERO_WEIGHT;
  }
  const isOnLeave =
    pic.leaveStart !== null && dateSgt >= pic.leaveStart && (pic.leaveEnd === null || dateSgt <= pic.leaveEnd);
  return isOnLeave ? PicDutyStatus.ON_LEAVE : null;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/** Milliseconds from `at` until the next SGT midnight, when the coverage day rolls over. */
export const msUntilNextSgtMidnight = (at: Date): number => {
  const sgtMs = at.getTime() + SGT_OFFSET_MS;
  return DAY_MS - (((sgtMs % DAY_MS) + DAY_MS) % DAY_MS);
};

/** Groups the roster by purpose, listing a PIC under every purpose it holds, and marks who the Facade would consider on duty at `at` (SGT calendar day). */
export const buildPicRosterCoverage = (pics: FulfilmentPicProfile[], at: Date): PicRosterCoverage => {
  const sgt = new Date(at.getTime() + SGT_OFFSET_MS);
  const dateSgt = sgt.toISOString().slice(0, 10);
  const weekday = WEEKDAYS_FROM_SUNDAY[sgt.getUTCDay()]!;
  const isWeekend = weekday === FulfilmentPicWeekday.SAT || weekday === FulfilmentPicWeekday.SUN;

  const available = pics.filter((pic) => unavailableStatus(pic, dateSgt) === null);
  const weekendOverrides = isWeekend ? available.filter((pic) => pic.weekendAssignments.includes(dateSgt)) : [];
  const onDuty = new Set(
    weekendOverrides.length > 0 ? weekendOverrides : available.filter((pic) => pic.activeDays.includes(weekday)),
  );

  const dutyOf = (pic: FulfilmentPicProfile): PicDuty => ({
    pic,
    status: unavailableStatus(pic, dateSgt) ?? (onDuty.has(pic) ? PicDutyStatus.ON_DUTY : PicDutyStatus.OFF_TODAY),
  });

  const groupOf = (purpose: FulfilmentPicPurpose | null): PicPurposeCoverage => {
    const members = pics
      .filter((pic) => {
        const picPurposes = resolvePicPurposes(pic);
        return purpose === null ? picPurposes.length === 0 : picPurposes.includes(purpose);
      })
      .map(dutyOf);
    return { purpose, members, hasMemberOnDuty: members.some((member) => member.status === PicDutyStatus.ON_DUTY) };
  };

  return {
    dateSgt,
    groups: [...Object.values(FulfilmentPicPurpose).map(groupOf), groupOf(null)],
  };
};
